/**
 * Positions Management Hook (HYBRID MODE)
 *
 * React hook for managing portfolio positions (Buy/Sell) with HYBRID storage:
 * - LOCAL mode: Uses localStorage (default, no auth required)
 * - CLOUD mode: Uses Supabase (optional, when user enables cloud sync)
 *
 * Handles FIFO/LIFO/Average Cost calculations
 * Backward compatible with existing localStorage-based positions
 */

import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase/client';
import { StorageService } from '../services/storage';
import { useHybridStorage } from './useHybridStorage';
import { PortfolioManager } from '../services/portfolioManager';
import type { Database } from '../lib/supabase/types';

// Supabase types
type Position = Database['public']['Tables']['portfolio_positions']['Row'];
type PositionInsert = Database['public']['Tables']['portfolio_positions']['Insert'];
type PositionUpdate = Database['public']['Tables']['portfolio_positions']['Update'];

// V2.G12.1: Supabase generated types declare numeric columns as `string`
// because PG `numeric` serializes that way. Callers in app code naturally
// pass `number` (we coerce internally via `String(...)`); widen the input
// types here so the call-site doesn't need to coerce manually.
type NumericInput = string | number;
type PositionInputBase = Omit<Partial<PositionInsert>,
  | 'quantity' | 'price' | 'fee' | 'original_cost_basis'
  | 'mining_hashrate' | 'staking_apy' | 'bridge_fee' | 'gas_fee_usd'
  | 'slippage_tolerance' | 'actual_slippage'
> & {
  quantity?: NumericInput;
  price?: NumericInput;
  fee?: NumericInput | null;
  original_cost_basis?: NumericInput | null;
  mining_hashrate?: NumericInput | null;
  staking_apy?: NumericInput | null;
  bridge_fee?: NumericInput | null;
  gas_fee_usd?: NumericInput | null;
  slippage_tolerance?: NumericInput | null;
  actual_slippage?: NumericInput | null;
};
export type PositionAddInput = PositionInputBase & { tokenId?: string };
export type PositionUpdateInput = Omit<Partial<PositionUpdate>,
  | 'quantity' | 'price' | 'fee' | 'original_cost_basis'
  | 'mining_hashrate' | 'staking_apy' | 'bridge_fee' | 'gas_fee_usd'
  | 'slippage_tolerance' | 'actual_slippage'
> & {
  quantity?: NumericInput;
  price?: NumericInput;
  fee?: NumericInput | null;
  original_cost_basis?: NumericInput | null;
  mining_hashrate?: NumericInput | null;
  staking_apy?: NumericInput | null;
  bridge_fee?: NumericInput | null;
  gas_fee_usd?: NumericInput | null;
  slippage_tolerance?: NumericInput | null;
  actual_slippage?: NumericInput | null;
};

// Local storage format (backward compatible)
export interface LocalPosition {
  id: string;
  tokenId: string;
  type: 'buy' | 'sell' | 'mining' | 'staking';
  date: string;
  quantity: number;
  price: number;
  note?: string;
  portfolioId?: string;
  // ✨ Cost Basis Chain Tracking
  is_swap?: boolean;
  swap_chain_id?: string;
  original_cost_basis?: number;
  // 💎 Mining Fields
  mining_pool?: string;
  mining_hashrate?: number;
  // 🔒 Staking Fields
  staking_apy?: number;
  staking_duration?: number;
  staking_cooldown?: number;
  staking_platform?: string;
  staking_end_date?: string;
  staking_is_locked?: boolean;
}

export interface UsePositionsReturn {
  positions: (Position | LocalPosition)[];
  loading: boolean;
  error: Error | null;
  mode: 'local' | 'cloud'; // Expose current mode

  // CRUD operations
  addPosition: (data: PositionAddInput) => Promise<Position | LocalPosition>;
  updatePosition: (id: string, updates: PositionUpdateInput | Partial<LocalPosition>) => Promise<Position | LocalPosition>;
  deletePosition: (id: string) => Promise<void>;

  // Queries
  getPositionsForToken: (tokenId: string) => (Position | LocalPosition)[];
  getPositionsByType: (type: 'buy' | 'sell') => (Position | LocalPosition)[];
  getPositionsByDateRange: (from: Date, to: Date) => (Position | LocalPosition)[];
}

/**
 * Hook for managing portfolio positions (HYBRID MODE)
 *
 * @param portfolioId - Optional portfolio ID (required in cloud mode)
 *
 * @example
 * ```tsx
 * function PositionsList({ tokenId }: { tokenId: string }) {
 *   const {
 *     positions,
 *     mode,
 *     addPosition,
 *     updatePosition,
 *     deletePosition,
 *     getPositionsForToken,
 *   } = usePositions();
 *
 *   const tokenPositions = getPositionsForToken(tokenId);
 *
 *   const handleAddBuy = async () => {
 *     await addPosition({
 *       tokenId, // or token_id in cloud mode
 *       type: 'buy',
 *       date: '2025-01-01',
 *       quantity: 0.5,
 *       price: 50000,
 *       note: 'Bought during dip',
 *     });
 *   };
 *
 *   return (
 *     <div>
 *       <p>Mode: {mode}</p>
 *       {tokenPositions.map(pos => <div key={pos.id}>...</div>)}
 *     </div>
 *   );
 * }
 * ```
 */
export function usePositions(portfolioId?: string): UsePositionsReturn {
  const { mode, userId, isInitialized } = useHybridStorage();
  const [positions, setPositions] = useState<(Position | LocalPosition)[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Track if initial load is complete - don't show loading spinner on background refetch
  const hasInitiallyLoaded = useRef(false);

  // Fetch positions on mount and when mode changes
  // IMPORTANT: Wait for isInitialized to prevent race conditions
  useEffect(() => {
    let initTimeout: NodeJS.Timeout | undefined;
    let isCancelled = false;

    if (!isInitialized) {
      console.log('[usePositions] Waiting for auth initialization...');

      // Safety: If initialization takes too long (10s), force load in local mode
      initTimeout = setTimeout(() => {
        if (isCancelled) return;
        console.warn('[usePositions] Auth init timeout - forcing local mode fetch');
        setLoading(false);
        hasInitiallyLoaded.current = true;
        // Try to load from PortfolioManager as fallback
        if (portfolioId) {
          const pmPortfolio = PortfolioManager.getPortfolio(portfolioId);
          if (pmPortfolio?.tokens) {
            const allPositions: LocalPosition[] = [];
            pmPortfolio.tokens.forEach(token => {
              token.positions?.forEach(pos => {
                allPositions.push({
                  id: pos.id,
                  tokenId: token.id,
                  type: pos.type,
                  date: pos.date,
                  quantity: pos.quantity,
                  price: pos.price,
                  note: pos.note,
                  portfolioId,
                });
              });
            });
            setPositions(allPositions);
          }
        }
      }, 10000);

      return () => {
        isCancelled = true;
        if (initTimeout) clearTimeout(initTimeout);
      };
    }

    console.log('[usePositions] Auth initialized, fetching positions...', { mode, userId, portfolioId });
    fetchPositions();

    // Subscribe to realtime changes (CLOUD MODE ONLY)
    if (mode === 'cloud' && portfolioId) {
      console.log('[CLOUD] Setting up realtime subscription for positions...');

      const channel = supabase
        .channel('portfolio_positions_changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'portfolio_positions',
            filter: `portfolio_id=eq.${portfolioId}`,
          },
          (payload) => {
            console.log('[CLOUD] Position change detected:', payload);

            // V2.H.7.1 fix : `payload.new` is the raw row from
            // postgres_changes — it does NOT include the `coin_id` join
            // that fetchPositions/addPosition/updatePosition enrich
            // manually. Merging instead of replacing preserves coin_id
            // and any other client-side enrichment (avoids the data-loss
            // bug where a row "disappeared" after an UPDATE event).
            if (payload.eventType === 'INSERT') {
              setPositions((prev) => {
                // De-dupe : optimistic addPosition may have added it already.
                if (prev.some((p) => p.id === payload.new.id)) return prev;
                return [...prev, payload.new as Position];
              });
            } else if (payload.eventType === 'UPDATE') {
              setPositions((prev) =>
                prev.map((p) =>
                  p.id === payload.new.id
                    ? ({ ...p, ...payload.new } as Position)
                    : p,
                ),
              );
            } else if (payload.eventType === 'DELETE') {
              setPositions((prev) => prev.filter((p) => p.id !== payload.old.id));
            }
          }
        )
        .subscribe();

      return () => {
        console.log('[CLOUD] Unsubscribing from positions realtime...');
        channel.unsubscribe();
      };
    }
  }, [portfolioId, mode, userId, isInitialized]);

  /**
   * Fetch positions (from localStorage or Supabase)
   */
  const fetchPositions = async () => {
    console.log('🔄 [fetchPositions] START', { mode, portfolioId, userId, hasInitiallyLoaded: hasInitiallyLoaded.current });

    try {
      // Only show loading spinner on INITIAL load, not background refetch
      if (!hasInitiallyLoaded.current) {
        setLoading(true);
      }
      setError(null);

      if (mode === 'cloud' && userId) {
        // CLOUD MODE: Fetch from Supabase
        // IMPORTANT: Join with portfolio_tokens to get coin_id for proper token matching
        // Bug fix (May 2026, v5) : STALE-WHILE-REVALIDATE.
        // Show cached positions immediately, then revalidate. If the
        // cloud query returns empty (race with Supabase client init),
        // KEEP the cache instead of wiping the user's data.
        const cachedPositions = StorageService.get<Position[]>('positions');
        if (cachedPositions && cachedPositions.length > 0) {
          console.log('[CLOUD] Showing cached positions immediately:', cachedPositions.length);
          setPositions(cachedPositions);
        }

        console.log('[CLOUD] Fetching positions from Supabase with coin_id...');

        // Bug fix (May 2026): wrap the Supabase query in `Promise.race` with
        // a 5 s timeout, mirroring the pattern in `usePortfolio.fetchPortfolios`.
        // Without this guard, a hung query (auth race, RLS slowness, network
        // stall) leaves `loading=true` forever — symptom : portfolio
        // skeleton infini + home spinner infini after a browser refresh.
        const buildTimeout = () => new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error('Positions fetch timeout (5s)')), 5000);
        });

        // Helper to load LocalPosition[] from PortfolioManager — used when the
        // cloud query times out or returns empty. Extracted to keep the two
        // query branches DRY.
        const loadFromPortfolioManager = (pid: string): LocalPosition[] => {
          let pmPortfolio = PortfolioManager.getPortfolio(pid);
          if (!pmPortfolio || !pmPortfolio.tokens) {
            const localActiveId = PortfolioManager.getActivePortfolioId() || 'default';
            pmPortfolio = PortfolioManager.getPortfolio(localActiveId);
          }
          if (!pmPortfolio?.tokens) return [];
          const localPositions: LocalPosition[] = [];
          pmPortfolio.tokens.forEach(token => {
            if (!token.positions?.length) return;
            token.positions.forEach(pos => {
              localPositions.push({
                id: pos.id,
                tokenId: token.id,
                type: pos.type,
                date: pos.date,
                quantity: pos.quantity,
                price: pos.price,
                note: pos.note,
                portfolioId: pid,
                is_swap: (pos as any).is_swap,
                swap_chain_id: (pos as any).swap_chain_id,
                original_cost_basis: (pos as any).original_cost_basis,
                mining_pool: (pos as any).mining_pool,
                mining_hashrate: (pos as any).mining_hashrate,
                staking_apy: (pos as any).staking_apy,
                staking_duration: (pos as any).staking_duration,
                staking_cooldown: (pos as any).staking_cooldown,
                staking_platform: (pos as any).staking_platform,
                staking_end_date: (pos as any).staking_end_date,
                staking_is_locked: (pos as any).staking_is_locked,
              });
            });
          });
          return localPositions;
        };

        if (!portfolioId) {
          console.warn('[CLOUD] No portfolio ID provided, fetching all user positions');
          const fetchPromise = supabase
            .from('portfolio_positions')
            .select('*, portfolio_tokens!inner(coin_id)')
            .eq('user_id', userId)
            .order('date', { ascending: false });

          let data: any, fetchError: any;
          try {
            const result = await Promise.race([fetchPromise, buildTimeout()]);
            data = result.data;
            fetchError = result.error;
          } catch (timeoutErr: any) {
            console.warn('[CLOUD] Positions fetch timeout (no portfolioId) — falling back to local cache');
            const cached = StorageService.get<Position[]>('positions', []);
            setPositions(cached);
            return;
          }

          if (fetchError) throw fetchError;

          // Flatten the result to include coin_id directly on position
          const positionsWithCoinId = (data || []).map((pos: any) => ({
            ...pos,
            coin_id: pos.portfolio_tokens?.coin_id,
          }));

          console.log('[CLOUD] Positions fetched with coin_id:', positionsWithCoinId.length);

          // Don't wipe cache with empty result.
          if (positionsWithCoinId.length === 0 && cachedPositions && cachedPositions.length > 0) {
            console.warn('[CLOUD] Empty positions but cache has data — keeping cache');
          } else {
            setPositions(positionsWithCoinId);
            if (positionsWithCoinId.length > 0) {
              StorageService.set('positions', positionsWithCoinId);
            }
          }
        } else {
          // Helper used by the retry-on-empty path below.
          const buildPositionsQuery = () =>
            supabase
              .from('portfolio_positions')
              .select('*, portfolio_tokens!inner(coin_id)')
              .eq('portfolio_id', portfolioId)
              .order('date', { ascending: false });

          let data: any, fetchError: any;
          try {
            const result = await Promise.race([buildPositionsQuery(), buildTimeout()]);
            data = result.data;
            fetchError = result.error;

            // Bug fix (May 2026): retry once on empty result. Same race as
            // usePortfolio — Supabase client may not have synced its session
            // yet when fetchPositions fires immediately after the
            // useHybridStorage fast path. Retry after 1 s gives the client
            // time to attach the bearer token. Matches the pattern that
            // makes useWallets (which fires later) work today.
            if ((!data || data.length === 0) && !fetchError && !hasInitiallyLoaded.current) {
              console.log('[CLOUD] Empty positions on first try — retrying after 1 s');
              await new Promise(resolve => setTimeout(resolve, 1000));
              try {
                const retryResult = await Promise.race([buildPositionsQuery(), buildTimeout()]);
                if (retryResult.data && retryResult.data.length > 0) {
                  console.log('[CLOUD] Positions retry succeeded with', retryResult.data.length, 'rows');
                  data = retryResult.data;
                  fetchError = null;
                } else {
                  console.log('[CLOUD] Positions retry also empty');
                }
              } catch {
                console.warn('[CLOUD] Positions retry timed out');
              }
            }
          } catch (timeoutErr: any) {
            console.warn('[CLOUD] Positions fetch timeout — falling back to PortfolioManager local data');
            const localPositions = loadFromPortfolioManager(portfolioId);
            setPositions(localPositions);
            return;
          }

          if (fetchError) throw fetchError;

          // Flatten the result to include coin_id directly on position
          const positionsWithCoinId = (data || []).map((pos: any) => ({
            ...pos,
            coin_id: pos.portfolio_tokens?.coin_id,
          }));

          console.log('[CLOUD] Positions fetched with coin_id:', positionsWithCoinId.length);

          // CRITICAL: Don't wipe cache with empty result (race-on-refresh).
          if (positionsWithCoinId.length === 0 && cachedPositions && cachedPositions.length > 0) {
            console.warn('[CLOUD] Empty positions but cache has data — keeping cache');
            return;
          }

          // FALLBACK: If cloud has no positions, try local data
          if (positionsWithCoinId.length === 0 && portfolioId) {
            console.log('[CLOUD] No cloud positions found, checking PortfolioManager local data...');
            const localPositions = loadFromPortfolioManager(portfolioId);
            if (localPositions.length > 0) {
              console.log('[CLOUD] Found local positions, using as fallback:', localPositions.length);
              setPositions(localPositions);
              return;
            }
          }

          setPositions(positionsWithCoinId);

          // Cache in localStorage (only when we have real data)
          if (positionsWithCoinId.length > 0) {
            StorageService.set('positions', positionsWithCoinId);
          }
        }
      } else {
        // LOCAL MODE: Fetch from PortfolioManager (positions are inside token.positions)
        console.log('[LOCAL] Fetching positions from PortfolioManager...');

        if (portfolioId) {
          const pmPortfolio = PortfolioManager.getPortfolio(portfolioId);
          if (pmPortfolio && pmPortfolio.tokens) {
            // Extract all positions from all tokens
            const allPositions: LocalPosition[] = [];
            pmPortfolio.tokens.forEach(token => {
              if (token.positions && token.positions.length > 0) {
                token.positions.forEach(pos => {
                  allPositions.push({
                    id: pos.id,
                    tokenId: token.id,
                    type: pos.type,
                    date: pos.date,
                    quantity: pos.quantity,
                    price: pos.price,
                    note: pos.note,
                    portfolioId: portfolioId,
                    // ✨ Cost Basis Chain Tracking - Preserve swap metadata (don't use || undefined to preserve false)
                    is_swap: (pos as any).is_swap,
                    swap_chain_id: (pos as any).swap_chain_id,
                    original_cost_basis: (pos as any).original_cost_basis,
                    // 💎 Mining Fields
                    mining_pool: (pos as any).mining_pool,
                    mining_hashrate: (pos as any).mining_hashrate,
                    // 🔒 Staking Fields
                    staking_apy: (pos as any).staking_apy,
                    staking_duration: (pos as any).staking_duration,
                    staking_cooldown: (pos as any).staking_cooldown,
                    staking_platform: (pos as any).staking_platform,
                    staking_end_date: (pos as any).staking_end_date,
                    staking_is_locked: (pos as any).staking_is_locked,
                  });
                });
              }
            });

            console.log('[LOCAL] Loaded positions from portfolio:', allPositions.length);
            setPositions(allPositions);
          } else {
            setPositions([]);
          }
        } else {
          // No portfolioId: try legacy 'positions' key (fallback)
          const localPositions = StorageService.get<LocalPosition[]>('positions', []);
          setPositions(localPositions);
        }
      }
    } catch (err) {
      setError(err as Error);
      console.error('Error fetching positions:', err);
    } finally {
      setLoading(false);
      hasInitiallyLoaded.current = true;
      console.log('🔄 [fetchPositions] DONE - Total positions loaded:', positions.length);
    }
  };

  /**
   * Add new position
   *
   * CLOUD MODE: Save directly to Supabase
   * LOCAL MODE: Save to PortfolioManager
   */
  const addPosition = async (
    data: PositionAddInput
  ): Promise<Position | LocalPosition> => {
    const tokenId = data.tokenId || data.token_id || '';
    console.log('🔵 [addPosition] START', { tokenId, type: data.type, mode, userId });

    try {
      setError(null);

      if (!portfolioId) {
        throw new Error('Portfolio ID required');
      }

      if (!tokenId) {
        throw new Error('Token ID required');
      }

      // ============================================
      // CLOUD MODE: OPTIMISTIC ADD (V2.H.7.2).
      // Same philosophy as updatePosition / deletePosition : the user
      // sees their new position INSTANTLY in the UI, then we sync to
      // Supabase in the background. No more "Saving..." spinner that
      // hangs when Supabase is slow.
      // ============================================
      if (mode === 'cloud' && userId) {
        console.log('[CLOUD] Optimistic position add + background sync...');

        // 1. Generate the position id client-side (Postgres uuid type
        //    accepts our UUID). This way we know the id BEFORE the cloud
        //    insert resolves, so the realtime INSERT echo can be deduped
        //    by id (handler at line ~220).
        const optimisticId = crypto.randomUUID();
        const nowIso = new Date().toISOString();

        // 2. Build the optimistic Position. token_id (the cloud_tokens
        //    UUID) isn't known yet — we'll patch it in once the cloud
        //    insert succeeds. The UI matches positions to tokens by
        //    coin_id, which we DO know, so display works fine.
        const optimisticPosition: any = {
          id: optimisticId,
          user_id: userId,
          portfolio_id: portfolioId,
          token_id: '', // patched after background insert
          coin_id: tokenId,
          type: data.type || 'buy',
          date: data.date || new Date().toISOString().split('T')[0],
          quantity: String(data.quantity || 0),
          price: String(data.price || 0),
          note: data.note || null,
          source: 'app',
          is_swap: (data as any).is_swap ?? null,
          swap_chain_id: (data as any).swap_chain_id ?? null,
          original_cost_basis: (data as any).original_cost_basis?.toString() ?? null,
          mining_pool: (data as any).mining_pool ?? null,
          mining_hashrate: (data as any).mining_hashrate?.toString() ?? null,
          staking_apy: (data as any).staking_apy?.toString() ?? null,
          staking_duration: (data as any).staking_duration ?? null,
          staking_cooldown: (data as any).staking_cooldown ?? null,
          staking_platform: (data as any).staking_platform ?? null,
          staking_end_date: (data as any).staking_end_date ?? null,
          staking_is_locked: (data as any).staking_is_locked ?? null,
          created_at: nowIso,
          updated_at: nowIso,
        };

        // 3. Optimistic local state + cache update.
        setPositions((prev) => [...prev, optimisticPosition]);
        const cached = StorageService.get<Position[]>('positions', []);
        StorageService.set('positions', [...cached, optimisticPosition]);

        // 4. BACKGROUND : look up cloud token UUID, then insert. If
        //    anything fails, log and keep the optimistic position.
        void (async () => {
          try {
            const { data: cloudToken, error: tokenError } = await supabase
              .from('portfolio_tokens')
              .select('id')
              .eq('user_id', userId)
              .eq('portfolio_id', portfolioId)
              .eq('coin_id', tokenId)
              .single();

            if (tokenError || !cloudToken) {
              console.warn('[CLOUD] Background token lookup failed:', tokenError);
              return;
            }

            const { error: insertError } = await supabase
              .from('portfolio_positions')
              .insert({
                ...optimisticPosition,
                token_id: cloudToken.id,
                // Strip our client-side coin_id flatten — Supabase rejects
                // unknown columns. The fetchPositions join reads it back
                // from portfolio_tokens.
                coin_id: undefined,
              });

            if (insertError) {
              console.warn('[CLOUD] Background position insert failed:', insertError);
              return;
            }

            // Patch the local row with the real token_id we just discovered.
            setPositions((prev) =>
              prev.map((p) =>
                p.id === optimisticId ? ({ ...p, token_id: cloudToken.id } as any) : p,
              ),
            );
            const cachedAfter = StorageService.get<Position[]>('positions', []);
            StorageService.set(
              'positions',
              cachedAfter.map((p) =>
                p.id === optimisticId ? ({ ...p, token_id: cloudToken.id } as any) : p,
              ),
            );

            console.log('[CLOUD] Position add synced ✓');
          } catch (err) {
            console.warn('[CLOUD] Background position add threw:', err);
          }
        })();

        return optimisticPosition as Position;
      }

      // ============================================
      // LOCAL MODE: Save to PortfolioManager
      // ============================================
      console.log('[LOCAL] Saving position to PortfolioManager...');

      const pmPortfolio = PortfolioManager.getPortfolio(portfolioId);
      if (!pmPortfolio) {
        throw new Error('Portfolio not found in PortfolioManager');
      }

      // Find the token
      const token = pmPortfolio.tokens.find(t => t.id === tokenId);
      if (!token) {
        throw new Error(`Token ${tokenId} not found in portfolio`);
      }

      // Create new position
      const newPosition: LocalPosition = {
        id: crypto.randomUUID(),
        tokenId: tokenId,
        type: data.type || 'buy',
        date: data.date || new Date().toISOString().split('T')[0],
        quantity: typeof data.quantity === 'string' ? parseFloat(data.quantity) : (data.quantity || 0),
        price: typeof data.price === 'string' ? parseFloat(data.price) : (data.price || 0),
        note: data.note || undefined,
        portfolioId: portfolioId,
        is_swap: (data as any).is_swap,
        swap_chain_id: (data as any).swap_chain_id,
        original_cost_basis: (data as any).original_cost_basis,
        mining_pool: (data as any).mining_pool,
        mining_hashrate: (data as any).mining_hashrate,
        staking_apy: (data as any).staking_apy,
        staking_duration: (data as any).staking_duration,
        staking_cooldown: (data as any).staking_cooldown,
        staking_platform: (data as any).staking_platform,
        staking_end_date: (data as any).staking_end_date,
        staking_is_locked: (data as any).staking_is_locked,
      };

      // Add position to token
      if (!token.positions) {
        token.positions = [];
      }
      token.positions.push(newPosition);

      // Save portfolio locally
      PortfolioManager.savePortfolio(pmPortfolio);

      console.log('[LOCAL] Position saved:', newPosition.id);

      // Update local state
      setPositions((prev) => [...prev, newPosition]);

      return newPosition;
    } catch (err) {
      console.error('[addPosition] Error:', err);
      setError(err as Error);
      throw err;
    }
  };

  /**
   * Sync a position to Supabase in background
   * This is fire-and-forget - errors don't block the UI
   */
  const syncPositionToCloud = async (position: LocalPosition, token: any) => {
    if (!userId || !portfolioId) return;

    console.log('[CLOUD SYNC] Starting background sync for position...');

    try {
      // Step 1: Ensure token exists in portfolio_tokens
      let cloudTokenId: string;

      // Check if token already exists in cloud
      const { data: existingToken } = await supabase
        .from('portfolio_tokens')
        .select('id')
        .eq('user_id', userId)
        .eq('coin_id', position.tokenId)
        .single();

      if (existingToken) {
        cloudTokenId = existingToken.id;
        console.log('[CLOUD SYNC] Token already exists in cloud:', cloudTokenId);
      } else {
        // Create token in cloud
        console.log('[CLOUD SYNC] Creating token in cloud...');

        // Get the cloud portfolio ID
        const { data: cloudPortfolio } = await supabase
          .from('portfolios')
          .select('id')
          .eq('user_id', userId)
          .eq('is_default', true)
          .single();

        if (!cloudPortfolio) {
          throw new Error('No default portfolio found in cloud');
        }

        const { data: newToken, error: tokenError } = await supabase
          .from('portfolio_tokens')
          .insert({
            user_id: userId,
            portfolio_id: cloudPortfolio.id,
            coin_id: position.tokenId,
            symbol: token.symbol || position.tokenId.toUpperCase(),
            display_name: token.name || token.symbol || position.tokenId,
          })
          .select()
          .single();

        if (tokenError) throw tokenError;
        cloudTokenId = newToken.id;
        console.log('[CLOUD SYNC] Token created in cloud:', cloudTokenId);
      }

      // Get the cloud portfolio ID
      const { data: cloudPortfolio } = await supabase
        .from('portfolios')
        .select('id')
        .eq('user_id', userId)
        .eq('is_default', true)
        .single();

      if (!cloudPortfolio) {
        throw new Error('No default portfolio found in cloud');
      }

      // Step 2: Create position in cloud
      const { error: positionError } = await supabase
        .from('portfolio_positions')
        .insert({
          user_id: userId,
          portfolio_id: cloudPortfolio.id,
          token_id: cloudTokenId,
          type: position.type,
          date: position.date,
          quantity: position.quantity.toString(),
          price: position.price.toString(),
          note: position.note || null,
          source: 'local_sync',
          // Swap metadata
          is_swap: position.is_swap ?? null,
          swap_chain_id: position.swap_chain_id ?? null,
          original_cost_basis: position.original_cost_basis ?? null,
          // Mining fields
          mining_pool: position.mining_pool ?? null,
          mining_hashrate: position.mining_hashrate ?? null,
          // Staking fields
          staking_apy: position.staking_apy ?? null,
          staking_duration: position.staking_duration ?? null,
          staking_cooldown: position.staking_cooldown ?? null,
          staking_platform: position.staking_platform ?? null,
          staking_end_date: position.staking_end_date ?? null,
          staking_is_locked: position.staking_is_locked ?? null,
        });

      if (positionError) throw positionError;

      console.log('[CLOUD SYNC] Position synced to cloud ✅');
    } catch (err) {
      console.error('[CLOUD SYNC] Failed:', err);
      // Don't rethrow - this is background sync, UI already has local data
    }
  };

  /**
   * Update position
   */
  const updatePosition = async (
    id: string,
    updates: PositionUpdateInput | Partial<LocalPosition>
  ): Promise<Position | LocalPosition> => {
    try {
      setError(null);

      if (mode === 'cloud' && userId) {
        // CLOUD MODE: OPTIMISTIC UPDATE pattern (V2.H.7.2).
        //
        // History of this branch :
        //  - Original : awaited Supabase .update().select().single().
        //    When Supabase was slow (auth refresh race, network),
        //    the modal hung on "Saving..." forever.
        //  - V2.H.7  : added 5 s Promise.race timeout. Stopped the
        //    infinite hang but introduced data-loss because we replaced
        //    the local row with the joined-less response (lost coin_id).
        //  - V2.H.7.1: merged response into existing row to preserve
        //    coin_id. Fixed the data-loss but the modal still failed
        //    visibly when Supabase was slow (5s timeout fired even
        //    though the write would eventually succeed).
        //  - V2.H.7.2 (here) : OPTIMISTIC. We update local state +
        //    cache IMMEDIATELY (instant UX), then fire-and-forget the
        //    Supabase write in the background. The user never sees a
        //    spinner; if cloud sync fails, we log it and the local
        //    change persists. Same UX as the V1 read path that solved
        //    the skeleton-infini bug.
        console.log('[CLOUD] Optimistic position update + background sync...');

        const optimisticUpdates = {
          ...updates,
          updated_at: new Date().toISOString(),
        };

        // 1. OPTIMISTIC : update local state immediately (merge, don't replace).
        let mergedPosition: Position | LocalPosition | null = null;
        setPositions((prev) =>
          prev.map((p) => {
            if (p.id !== id) return p;
            const merged = { ...p, ...optimisticUpdates } as Position | LocalPosition;
            mergedPosition = merged;
            return merged;
          }),
        );

        // 2. Update cache (merge, preserve coin_id).
        const cached = StorageService.get<Position[]>('positions', []);
        StorageService.set(
          'positions',
          cached.map(p =>
            p.id === id ? ({ ...p, ...optimisticUpdates } as Position) : p,
          ),
        );

        if (!mergedPosition) {
          throw new Error('Position not found in local state');
        }

        // 3. BACKGROUND : sync to Supabase. Don't block the UI on this.
        //    If it fails, the optimistic local change still stands —
        //    fetchPositions will reconcile on next mount.
        void (async () => {
          try {
            const { error: bgError } = await supabase
              .from('portfolio_positions')
              .update(optimisticUpdates)
              .eq('id', id);
            if (bgError) {
              console.warn('[CLOUD] Background position update failed:', bgError);
            } else {
              console.log('[CLOUD] Position update synced ✓');
            }
          } catch (err) {
            console.warn('[CLOUD] Background position update threw:', err);
          }
        })();

        return mergedPosition;
      } else {
        // LOCAL MODE: Update in PortfolioManager (token.positions)
        console.log('[LOCAL] Updating position in PortfolioManager...');

        if (!portfolioId) {
          throw new Error('Portfolio ID required');
        }

        const pmPortfolio = PortfolioManager.getPortfolio(portfolioId);
        if (!pmPortfolio) {
          throw new Error('Portfolio not found');
        }

        // Find the position in the portfolio's tokens
        let updatedPosition: LocalPosition | null = null;
        for (const token of pmPortfolio.tokens) {
          if (token.positions) {
            const posIndex = token.positions.findIndex(p => p.id === id);
            if (posIndex !== -1) {
              // Update position
              token.positions[posIndex] = { ...token.positions[posIndex], ...updates };
              updatedPosition = token.positions[posIndex] as LocalPosition;
              break;
            }
          }
        }

        if (!updatedPosition) {
          throw new Error('Position not found');
        }

        // Save portfolio
        PortfolioManager.savePortfolio(pmPortfolio);

        // Update local state
        setPositions((prev) =>
          prev.map((p) => (p.id === id ? updatedPosition : p))
        );

        return updatedPosition;
      }
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  };

  /**
   * Delete position
   */
  const deletePosition = async (id: string): Promise<void> => {
    try {
      setError(null);

      if (mode === 'cloud' && userId) {
        // CLOUD MODE: OPTIMISTIC DELETE (V2.H.7.2 — same pattern as update).
        // Remove from local state + cache immediately, sync to Supabase
        // in the background. Don't block the UI on Supabase latency.
        console.log('[CLOUD] Optimistic position delete + background sync...');

        setPositions((prev) => prev.filter((p) => p.id !== id));
        const cached = StorageService.get<Position[]>('positions', []);
        StorageService.set('positions', cached.filter(p => p.id !== id));

        void (async () => {
          try {
            const { error: bgError } = await supabase
              .from('portfolio_positions')
              .delete()
              .eq('id', id);
            if (bgError) {
              console.warn('[CLOUD] Background position delete failed:', bgError);
            } else {
              console.log('[CLOUD] Position delete synced ✓');
            }
          } catch (err) {
            console.warn('[CLOUD] Background position delete threw:', err);
          }
        })();
      } else {
        // LOCAL MODE: Delete from PortfolioManager (token.positions)
        console.log('[LOCAL] Deleting position from PortfolioManager...');

        if (!portfolioId) {
          throw new Error('Portfolio ID required');
        }

        const pmPortfolio = PortfolioManager.getPortfolio(portfolioId);
        if (!pmPortfolio) {
          throw new Error('Portfolio not found');
        }

        // Find and remove the position from the portfolio's tokens
        let found = false;
        for (const token of pmPortfolio.tokens) {
          if (token.positions) {
            const originalLength = token.positions.length;
            token.positions = token.positions.filter(p => p.id !== id);
            if (token.positions.length < originalLength) {
              found = true;
              break;
            }
          }
        }

        if (!found) {
          throw new Error('Position not found');
        }

        // Save portfolio
        PortfolioManager.savePortfolio(pmPortfolio);

        // Update local state
        setPositions((prev) => prev.filter((p) => p.id !== id));
      }
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  };

  /**
   * Get positions for specific token
   */
  const getPositionsForToken = (tokenId: string): (Position | LocalPosition)[] => {
    return positions.filter((p) => {
      // Handle both cloud (token_id) and local (tokenId) formats
      const posTokenId = 'token_id' in p ? p.token_id : p.tokenId;
      return posTokenId === tokenId;
    });
  };

  /**
   * Get positions by type (buy or sell)
   */
  const getPositionsByType = (type: 'buy' | 'sell'): (Position | LocalPosition)[] => {
    return positions.filter((p) => p.type === type);
  };

  /**
   * Get positions within date range
   */
  const getPositionsByDateRange = (from: Date, to: Date): (Position | LocalPosition)[] => {
    const fromStr = from.toISOString().split('T')[0];
    const toStr = to.toISOString().split('T')[0];

    return positions.filter((p) => {
      return p.date >= fromStr && p.date <= toStr;
    });
  };

  return {
    positions,
    loading,
    error,
    mode,
    addPosition,
    updatePosition,
    deletePosition,
    getPositionsForToken,
    getPositionsByType,
    getPositionsByDateRange,
  };
}
